import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { atom, useAtom } from "jotai";

import { T } from "@repo/transifex";
import { useSessionOrders } from "~/hooks/queries/orders";
import { useAppContext } from "~/providers/app";
import { Button, Dialog, DialogError, DialogLoading } from "~/components";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { useCloseSession } from "~/api/sessions/hooks/use-close-session";
import { getAuth } from "~/providers/store/auth";
import { useSession } from "~/api/sessions/hooks/use-session";

export const closeCashBalanceAtom = atom(0);

export const HeaderButtonCloseSession = () => {
  const { profileId } = getAuth();
  const { sessionId, tillId } = useParams();
  const navigate = useNavigate();
  const {
    hardware: { printer }
  } = useAppContext();

  const { data: session, status: sessionStatus } = useSession({
    sessionId,
    tillId
  });
  const { mutate: closeTillSession, status, error, reset } = useCloseSession();
  const { data: orders, status: ordersStatus } = useSessionOrders({
    sessionId
  });

  const [closeCashBalance, setCloseCashBalance] = useAtom(closeCashBalanceAtom);
  const [open, setOpen] = useState(false);

  if (ordersStatus !== "success" || sessionStatus !== "success") return null;

  const isSessionClosed = session.endedAt !== null;

  const handleConfirm = () => {
    if (!sessionId || !tillId || !profileId) {
      throw new Error("Profile not selected");
    }

    const startedCashBalance =
      session.startedFlow === "manual"
        ? session.startedMeta.cashBalance?.[orders.currency] ?? 0
        : 0;
    const expectedUnit = orders.totalCashPriceUnit + startedCashBalance;

    closeTillSession(
      {
        sessionId,
        tillId,
        currency: orders.currency,
        profileId,
        printerId: printer?.identifier ?? "",
        expectedCashBalance: expectedUnit,
        closeCashBalance
      },
      {
        onSuccess: () => {
          setCloseCashBalance(0);
          navigate("/settings/tills");
        }
      }
    );
  };

  return (
    <>
      {!isSessionClosed && (
        <Button
          className="ml-auto"
          onClick={() => {
            setOpen(true);
          }}
          type="submit"
          variant="danger"
        >
          <T _str="Close register" />
        </Button>
      )}

      <Dialog
        onOutsideClick={() => {
          setOpen(false);
        }}
        open={open}
        size="md"
      >
        <div className="flex flex-col items-center p-8">
          <p className="mb-8 text-xs uppercase text-danger-dark">
            <T _str="Close register" />
          </p>

          <div className="flex flex-col items-center gap-y-2 text-center">
            <p className="text-xl">
              <T _str="Do you wish to continue?" />
            </p>

            <p className="w-5/6 text-xs">
              <T _str="If you're finished you can confirm and close register" />
            </p>
          </div>

          <div className="mt-12 flex justify-between gap-x-5">
            <Button
              onClick={() => {
                setOpen(false);
              }}
              variant="danger-light"
            >
              <T _str="Cancel" />
            </Button>

            <Button onClick={handleConfirm} variant="primary-light">
              <T _str="Confirm" />
            </Button>
          </div>
        </div>
      </Dialog>

      {status === "pending" && <DialogLoading />}
      {status === "error" && (
        <DialogError
          error={<DisplayErrorMessage error={error} />}
          onOutsideClick={() => {
            reset();
            setOpen(false);
          }}
        />
      )}
    </>
  );
};
